import React from 'react';
import { Scale, Zap, Link2, Triangle } from 'lucide-react';
import { calculateDynamics } from '../data/tensionData';

const DinamicaEstacoes = ({ result }) => {
  const dynamics = calculateDynamics(result);

  // Se Coração Inteiro ou sem tensões significativas
  if (dynamics.isCoracaoInteiro && dynamics.isBalanced) {
    return (
      <div className="mt-8" data-testid="dinamica-estacoes">
        <SectionHeader />
        <div className="bg-gradient-to-br from-amber-900/20 to-indigo-900/20 rounded-2xl p-6 border border-amber-500/20">
          <p className="text-white/80 leading-relaxed text-center">
            Suas estações coexistem em harmonia. Não há tensões significativas entre os eixos — o que existe é uma dança equilibrada entre corpo, tempo, sentimento e sentido. O trabalho agora é sustentar essa integração com presença.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="dinamica-estacoes">
      <SectionHeader />

      {/* Intro */}
      <p className="text-white/60 text-sm leading-relaxed mb-6 text-center italic">
        Nenhuma estação existe sozinha. Quando uma se fortalece, outras podem se silenciar.
        Entender essas dinâmicas é o primeiro passo para a integração.
      </p>

      {/* Tensões Ativas */}
      {dynamics.tensions.length > 0 && (
        <div className="space-y-4 mb-8">
          {dynamics.tensions.map((tension) => (
            <TensionCard key={tension.id} tension={tension} />
          ))}
        </div>
      )}

      {/* Se não há tensões mas também não é Coração Inteiro */}
      {dynamics.tensions.length === 0 && !dynamics.isCoracaoInteiro && (
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-8">
          <p className="text-white/70 text-sm leading-relaxed text-center">
            Seus eixos raiz mantêm um equilíbrio relativo. As diferenças entre eles são sutis, o que indica uma base emocional estável. As leituras abaixo revelam nuances mais finas.
          </p>
        </div>
      )}

      {/* Duplas — Indicadores de Integração */}
      {dynamics.dualReadings.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-4 h-4 text-blue-400" />
            <h4 className="text-blue-400 text-sm font-semibold tracking-wide uppercase">
              Pontes entre Estações
            </h4>
          </div>
          <div className="space-y-3">
            {dynamics.dualReadings.map((dual) => (
              <DualCard key={dual.key} dual={dual} />
            ))}
          </div>
        </div>
      )}

      {/* Triplas — Indicadores de Convergência */}
      {dynamics.tripleReadings.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Triangle className="w-4 h-4 text-pink-400" />
            <h4 className="text-pink-400 text-sm font-semibold tracking-wide uppercase">
              Convergências
            </h4>
          </div>
          <div className="space-y-3">
            {dynamics.tripleReadings.map((triple) => (
              <TripleCard key={triple.key} triple={triple} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ====== SUB-COMPONENTES ======

const SectionHeader = () => (
  <div className="flex justify-center mb-6">
    <div className="bg-gradient-to-r from-slate-800/80 via-indigo-900/60 to-slate-800/80 px-6 py-3 rounded-full border border-indigo-500/20">
      <div className="flex items-center gap-3">
        <Scale className="w-5 h-5 text-indigo-400" />
        <div className="text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold text-white">Dinâmica das Estações</h3>
          <p className="text-white/50 text-xs">Tensões, integrações e regulação</p>
        </div>
      </div>
    </div>
  </div>
);

const TensionCard = ({ tension }) => {
  const isStrong = tension.intensity === 'forte';

  return (
    <div
      className={`rounded-2xl overflow-hidden border ${
        isStrong
          ? 'border-amber-500/30 bg-gradient-to-br from-amber-950/30 via-slate-900/40 to-indigo-950/30'
          : 'border-white/10 bg-gradient-to-br from-slate-900/40 to-indigo-950/20'
      }`}
      data-testid={`tension-${tension.id}`}
    >
      {/* Header da tensão */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap className={`w-4 h-4 ${isStrong ? 'text-amber-400' : 'text-indigo-400'}`} />
            <span className={`text-xs font-medium uppercase tracking-wider ${
              isStrong ? 'text-amber-400/80' : 'text-indigo-400/80'
            }`}>
              Tensão {tension.intensity}
            </span>
          </div>
          <span className="text-white/30 text-xs">{tension.natureza}</span>
        </div>
        
        {/* Barra visual da tensão */}
        <div className="flex items-center gap-3 mb-3">
          <span className={`text-sm font-semibold ${
            tension.direction === 'A' ? 'text-amber-300' : 'text-white/50'
          }`}>
            {tension.eixoA.name}
          </span>
          <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden flex">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-500/60 rounded-full transition-all"
              style={{ width: `${(tension.scoreA / 40) * 100}%` }}
            />
          </div>
          <span className="text-white/40 text-xs w-6 text-right">{tension.scoreA}</span>
        </div>
        <div className="flex items-center gap-3 mb-1">
          <span className={`text-sm font-semibold ${
            tension.direction === 'B' ? 'text-amber-300' : 'text-white/50'
          }`}>
            {tension.eixoB.name}
          </span>
          <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden flex">
            <div
              className="h-full bg-gradient-to-r from-slate-400 to-slate-500/60 rounded-full transition-all"
              style={{ width: `${(tension.scoreB / 40) * 100}%` }}
            />
          </div>
          <span className="text-white/40 text-xs w-6 text-right">{tension.scoreB}</span>
        </div>
      </div>

      {/* Conteúdo da leitura */}
      <div className="px-5 pb-5 pt-2">
        <h4 className="text-white font-semibold text-base mb-2 italic">
          "{tension.reading.titulo}"
        </h4>
        <p className="text-white/70 text-sm leading-relaxed mb-4">
          {tension.reading.sombra}
        </p>

        {/* Regulação */}
        <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3">
          <p className="text-white/50 text-xs font-medium uppercase tracking-wider">Caminho de regulação</p>
          <p className="text-white/80 text-sm leading-relaxed">
            {tension.reading.regulacao}
          </p>
          <div className="border-t border-white/10 pt-3">
            <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-1">Prática</p>
            <p className="text-white/70 text-sm leading-relaxed italic">
              {tension.reading.pratica}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const DualCard = ({ dual }) => {
  const isActive = dual.status === 'ativo';
  const pct = Math.round((dual.score / dual.maxScore) * 100);

  return (
    <div
      className={`rounded-xl p-4 border ${
        isActive
          ? 'bg-blue-500/8 border-blue-500/20'
          : 'bg-white/3 border-white/10'
      }`}
      data-testid={`dual-reading-${dual.key}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-semibold ${isActive ? 'text-blue-300' : 'text-white/50'}`}>
          {dual.name}
        </span>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            isActive
              ? 'bg-blue-500/20 text-blue-300'
              : 'bg-white/5 text-white/40'
          }`}>
            {isActive ? 'integrada' : 'distante'}
          </span>
          <span className="text-white/30 text-xs">{dual.score}/{dual.maxScore}</span>
        </div>
      </div>
      <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full rounded-full ${isActive ? 'bg-blue-400' : 'bg-white/20'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className={`text-sm leading-relaxed ${isActive ? 'text-white/70' : 'text-white/50'}`}>
        {dual.reading}
      </p>
    </div>
  );
};

const TripleCard = ({ triple }) => {
  const isActive = triple.status === 'ativo';
  const pct = Math.round((triple.score / triple.maxScore) * 100);

  return (
    <div
      className={`rounded-xl p-4 border ${
        isActive
          ? 'bg-pink-500/8 border-pink-500/20'
          : 'bg-white/3 border-white/10'
      }`}
      data-testid={`triple-reading-${triple.key}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-semibold ${isActive ? 'text-pink-300' : 'text-white/50'}`}>
          {triple.name}
        </span>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            isActive
              ? 'bg-pink-500/20 text-pink-300'
              : 'bg-white/5 text-white/40'
          }`}>
            {isActive ? 'convergente' : 'em formação'}
          </span>
          <span className="text-white/30 text-xs">{triple.score}/{triple.maxScore}</span>
        </div>
      </div>
      <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full rounded-full ${isActive ? 'bg-pink-400' : 'bg-white/20'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className={`text-sm leading-relaxed ${isActive ? 'text-white/70' : 'text-white/50'}`}>
        {triple.reading}
      </p>
      {isActive && triple.neglectedAxis && (
        <p className="text-amber-400/60 text-xs mt-2 italic">
          Eixo que pede atenção: {triple.neglectedAxis.name}
        </p>
      )}
    </div>
  );
};

export default DinamicaEstacoes;
